import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Lock } from "lucide-react";
import { useAuth } from "@/context/AppContext";
import { formatApiError } from "@/lib/api";
import SEO from "@/components/SEO";

export default function AdminLogin() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-forest-deep px-4 grain relative" data-testid="admin-login-page">
      <SEO title="Admin Login | AnVibes Exports" path="/admin/login" />
      <div className="w-full max-w-md border border-white/10 bg-forest p-10">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center bg-gold/15 border border-gold/30">
            <Lock className="h-5 w-5 text-gold" />
          </span>
          <div>
            <p className="font-serif text-2xl text-parchment">AnVibes<span className="text-gold text-sm align-super">™</span> Admin</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-parchment/50">AnVibes Exports</p>
          </div>
        </div>
        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="admin-login-form">
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-parchment/50 mb-1.5" htmlFor="admin-email">Email</label>
            <input
              id="admin-email"
              data-testid="admin-login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full border border-white/15 bg-forest-deep px-4 py-3 text-sm text-parchment placeholder:text-parchment/30 focus:border-gold focus:outline-none"
              placeholder="admin@anvibesexports.com"
            />
          </div>
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-[0.22em] text-parchment/50 mb-1.5" htmlFor="admin-password">Password</label>
            <input
              id="admin-password"
              data-testid="admin-login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full border border-white/15 bg-forest-deep px-4 py-3 text-sm text-parchment placeholder:text-parchment/30 focus:border-gold focus:outline-none"
              placeholder="••••••••"
            />
          </div>
          {error && <p className="border border-terracotta/50 bg-terracotta/15 px-4 py-2.5 text-sm text-[#F0B9A4]" data-testid="admin-login-error">{error}</p>}
          <button
            type="submit"
            disabled={loading}
            data-testid="admin-login-button"
            className="w-full bg-gold px-6 py-3.5 text-sm font-semibold uppercase tracking-[0.16em] text-forest transition-colors hover:bg-gold-light disabled:opacity-60"
          >
            {loading ? <Loader2 className="mx-auto h-4 w-4 animate-spin" /> : "Sign In"}
          </button>
        </form>
      </div>
    </div>
  );
}
